import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useFunctions, useStorage } from "reactfire";
import { Country } from "../../../../../shared/constants/Constant";
import FormValidator from "../../../../../shared/utils/FormValidator";
import PageHeader from "../../../../dashboard/components/page-header/PageHeader";
import random from "randomstring";
import { DashboardMentoringPages } from "../../Mentoring";
import UserNotification from "../../../../../shared/components/Notification/UserNotification";
import { BarLoader } from "react-spinners";
import TextEditor from "../../../../../shared/components/text-editor/TextEditor";
import SelectCountry from "../../../../../shared/components/countrySelectorModal/SelectCountry";
import { useAppUser } from "../../../../../contexts/user-context/UserContext";
import CountryTag from '../../components/country-tag/CountryTag';

interface MentorProfile {
  fullName: string;
  email: string;
  phoneNumber: string;
  qualification: string;
  designation: string;
  university: string;
  bio: string;
  countries: Country[];
  facebookURL?: string;
  linkedinURL?: string;
  twitterURL?: string;
  websiteURL?: string;
  loading: boolean;
  fullNameError: string;
  emailError: string;
  phoneNumberError?: string;
  universityError: string;
  bioError: string;
  facebookURLError?: string;
  linkedinURLError?: string;
  twitterURLError?: string;
  websiteURLError?: string;
  thumbnailError?: string;
  countriesError: string;
}

const initMentorProfile: MentorProfile = {
  fullName: "",
  email: "",
  phoneNumber: "",
  qualification: "",
  designation: "",
  university: "",
  bio: "",
  countries: [],
  facebookURL: "",
  linkedinURL: "",
  twitterURL: "",
  websiteURL: "",
  loading: false,
  fullNameError: "",
  emailError: "",
  phoneNumberError: "",
  universityError: "",
  bioError: "",
  facebookURLError: "",
  linkedinURLError: "",
  twitterURLError: "",
  websiteURLError: "",
  thumbnailError: "",
  countriesError: "",
};

const MentorProfileCreate = () => {
  const creatRef = useFunctions().httpsCallable("addMentor");
  const history = useHistory();
  const [mentorProfile, setMentorProfile] = useState(initMentorProfile);
  const [fileInput, setFileInput] = useState<any>(null);
  const [thumbnail, setThumbnail] = useState<{
    file: File | null;
    preview: any;
  }>({ file: null, preview: null });

  const storage = useStorage().ref();
  const [modalCountrySelectorShow, setModalCountrySelectorShow] = useState(
    false
  );

  const countries = Object.values(Country);
  const [selectedCountries, setSelectedCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>();

  const appUser = useAppUser();

  const selectCountry = (country: Country) => {
    setSelectedCountries((ps) => {
      if (ps.filter((c) => c === country).length === 0) {
        ps.push(country);
      }
      return Object.assign([], ps);
    });
  };

  const deSelectCountry = (country: Country) => {
    setSelectedCountries((ps) => {
      ps = ps.filter((c) => c !== country);
      return Object.assign([], ps);
    });
  };

  useEffect(() => {
    let available = true;
    if (available) {
      setMentorProfile((prevState) => {
        return Object.assign(
          {},
          {
            fullName: prevState.fullName,
            email: prevState.email,
            phoneNumber: prevState.phoneNumber,
            qualification: prevState.qualification,
            designation: prevState.designation,
            countries: selectedCountries,
            university: prevState.university,
            bio: prevState.bio,
            facebookURL: prevState.facebookURL,
            linkedinURL: prevState.linkedinURL,
            loading: prevState.loading,
            twitterURL: prevState.twitterURL,
            websiteURL: prevState.websiteURL,
            fullNameError: prevState.fullNameError,
            emailError: prevState.emailError,
            phoneNumberError: prevState.phoneNumberError,
            universityError: prevState.universityError,
            bioError: prevState.bioError,
            facebookURLError: prevState.facebookURLError,
            linkedinURLError: prevState.linkedinURLError,
            twitterURLError: prevState.twitterURLError,
            websiteURLError: prevState.websiteURLError,
            thumbnailError: prevState.thumbnailError,
            countriesError: prevState.countriesError
          }
        );
      });
    }

    return () => {
      available = false;
    };
  }, [selectedCountries]);

  const buttonSet = (
    <Row style={{ float: "right" }} className="p-2">
      <Col xs={6}>
        <div
          className={
            mentorProfile.loading
              ? "cancel-button grayStatic"
              : "cancel-button blue"
          }
          onClick={() => {
            !mentorProfile.loading &&
              history.push(DashboardMentoringPages.MENTOR_PROFILES);
          }}
        >
          Cancel
        </div>
      </Col>
      <Col xs={6}>
        <div
          className={
            mentorProfile.loading ? "save-button grayStatic" : "save-button"
          }
          onClick={() => {
            !mentorProfile.loading &&
              validateCreateCurriculumFormData() &&
              createMentorProfile();
          }}
        >
          Save
        </div>
      </Col>
    </Row>
  );

  const createMentorProfile = () => {
    if (thumbnail.file) {
      setMentorProfile({
        ...mentorProfile,
        loading: true,
      });
      const uploadTask = storage
        .child(
          `mentorprofilepictures/${random.generate(10)}.${thumbnail?.file.name
            .split(".")
            .pop()
            ?.toLowerCase()}`
        )
        .put(thumbnail.file);
      uploadTask.on("state_changed", function (snapshot) {
        let progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        console.log(progress);
      });
      uploadTask.then((snapshot) => {
        snapshot.ref.getDownloadURL().then((url) => {
          const reqBody = {
            fullName: mentorProfile.fullName,
            email: mentorProfile.email,
            phoneNumber: mentorProfile.phoneNumber,
            qualification: mentorProfile.qualification || "",
            designation: mentorProfile.designation || "",
            bio: mentorProfile.bio,
            countries: mentorProfile.countries,
            photoURL: url,
          };
          creatRef(reqBody)
            .then((res) => {
              if (res.data.data) {
                UserNotification({
                  isSuccess: true,
                  message: "Mentor profile creation successful!",
                });
                history.push(DashboardMentoringPages.MENTOR_PROFILES);
              } else {
                UserNotification({
                  isSuccess: false,
                  message: "Mentor profile creation failed!",
                });
              }
            })
            .catch((error) => {
              console.log("error", error);
              UserNotification({
                isSuccess: false,
                message:
                  "Mentor profile creation failed!\nPlease check your connection.",
              });
            })
            .finally(() => {
              setMentorProfile({
                ...mentorProfile,
                loading: false,
              });
            });
        });
      });

      uploadTask.catch(() => {
        UserNotification({
          isSuccess: false,
          message: "Mentor profile creation failed!",
        });
        setMentorProfile({
          ...mentorProfile,
          loading: false,
        });
      });
    } else {
      setMentorProfile({
        ...mentorProfile,
        loading: true,
      });
      const reqBody = {
        fullName: mentorProfile.fullName,
        email: mentorProfile.email,
        phoneNumber: mentorProfile.phoneNumber,
        qualification: mentorProfile.qualification || "",
        designation: mentorProfile.designation || "",
        bio: mentorProfile.bio,
        countries: mentorProfile.countries,
        photoURL: "",
      };
      creatRef(reqBody)
        .then((res) => {
          if (res.data.data) {
            UserNotification({
              isSuccess: true,
              message: "Mentor profile creation successful!",
            });
            history.push(DashboardMentoringPages.MENTOR_PROFILES);
          } else {
            UserNotification({
              isSuccess: false,
              message: "Mentor profile creation failed!",
            });
          }
        })
        .catch((error) => {
          console.log("error", error, reqBody);
          UserNotification({
            isSuccess: false,
            message:
              "Mentor profile creation failed!\nPlease check your connection.",
          });
        })
        .finally(() => {
          setMentorProfile({
            ...mentorProfile,
            loading: false,
          });
        });
    }
  };

  const validateCreateCurriculumFormData = (): boolean => {
    const fnValidation = FormValidator.validateString(
      mentorProfile.fullName,
      "Full Name",
      3,
      100
    );

    const profileImageValidation =
      thumbnail.file !== null
        ? { message: "", isValid: true }
        : { message: "Please set a profile picture", isValid: false };

    const bioValidation =
      mentorProfile.bio === undefined || mentorProfile.bio === ""
        ? { message: "", isValid: true }
        : FormValidator.validateQuestionBody(mentorProfile.bio, "Bio", 3, 2100);

    const countriesValidation =
      mentorProfile.countries === undefined || mentorProfile.countries.length === 0
        ? { message: "Please select a country", isValid: false }
        : { message: "", isValid: true }

    setMentorProfile({
      ...mentorProfile,
      fullNameError: fnValidation.message,
      bioError: bioValidation.message,
      thumbnailError: profileImageValidation.message,
      countriesError: countriesValidation.message,
    });

    return (
      fnValidation.isValid &&
      profileImageValidation.isValid &&
      bioValidation.isValid &&
      countriesValidation.isValid
    );
  };

  const onEditorUpdates = (content: any, editor: any) => {
    if (content !== undefined) {
      setMentorProfile({ ...mentorProfile, bio: content, bioError: "" });
    }
  };

  return (
    <div>
      <PageHeader title="Create Mentor Profile" />
      <Container
        fluid
        style={{
          paddingTop: "200px",
          paddingLeft: "50px",
          paddingRight: "50px",
        }}
      >
        <Row>
          <Col
            xs={12}
            className="text-left font-weight-bold"
            style={{ color: "rgb(7, 66, 94)", width: "100%" }}
          >
            <label style={{ color: "#505050", fontWeight: "bold" }}>
              Full Name{" "}
              <span className="ml-1" style={{ color: "red" }}>
                *
              </span>
            </label>
            <input
              className={
                mentorProfile.fullNameError.length < 1
                  ? "app-input"
                  : "app-input invalid"
              }
              required={true}
              onChange={(e) => {
                setMentorProfile({
                  ...mentorProfile,
                  fullName: e.target.value,
                  fullNameError: "",
                });
              }}
              value={mentorProfile.fullName}
            />
            <label className="error-message">
              {mentorProfile.fullNameError}
            </label>
          </Col>
        </Row>
        <Row >
          <Col xs={12} md={6}>
            <label style={{ color: "#505050", fontWeight: "bold" }}>
              Qualification
            </label>
            <input
              className="app-input"
              onChange={(e) => {
                setMentorProfile({
                  ...mentorProfile,
                  qualification: e.target.value
                });
              }}
              value={mentorProfile.qualification}
            />
            <label className="error-message" />
          </Col>
          <Col xs={12} md={6}>
            <label style={{ color: "#505050", fontWeight: "bold" }}>
              Designation
            </label>
            <input
              className="app-input"
              onChange={(e) => {
                setMentorProfile({
                  ...mentorProfile,
                  designation: e.target.value
                });
              }}
              value={mentorProfile.designation}
            />
            <label className="error-message" />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <label
              style={{ color: "#505050", fontWeight: "bold" }}
            >
              Countries <span className="ml-1" style={{ color: "red" }}>
                *
              </span>
            </label>
            <div>
              <select
                className={
                  mentorProfile.countriesError.length < 1
                    ? "app-input"
                    : "app-input invalid"
                }
                required={true}
                value={
                  selectedCountry ? selectedCountry : "select"
                }
                onChange={(e) => {
                  setMentorProfile({ ...mentorProfile, countriesError: "" });
                  setSelectedCountry(e.target.value);
                  const [country] = countries.filter(
                    (c) => c === e.target.value
                  );
                  selectCountry(country);
                }}
              >
                <option value="select" disabled hidden>
                  Select Countries
                </option>
                {countries.map((c, index) => (
                  <option key={index} value={c}>
                    {c.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
            <label className="error-message" > {mentorProfile.countriesError}  </label>
          </Col>
          <Col xs={12} md={6}>
            <label
              style={{ color: "#505050", fontWeight: "bold" }}
            >
              Selected Countries
            </label>
            <div>
              {selectedCountries.map((c, index) => (
                <CountryTag
                  key={index}
                  country={c}
                  onClose={(c) => {
                    deSelectCountry(c);
                  }}
                />
              ))}
            </div>
          </Col>
        </Row>
        {/*<Row*/}
        {/*  style={{*/}
        {/*    marginBottom:*/}
        {/*      mentorProfile.qualification.length < 1 &&*/}
        {/*      mentorProfile.designation.length < 1*/}
        {/*        ? "10px"*/}
        {/*        : "0px",*/}
        {/*  }}*/}
        {/*>*/}
        {/*  <Col xs={12} md={6}>*/}
        {/*    <label style={{color: "#505050", fontWeight: "bold"}}>*/}
        {/*      Email*/}
        {/*    </label>*/}
        {/*    <input*/}
        {/*      className="app-input"*/}
        {/*      onChange={(e) => {*/}
        {/*        setMentorProfile({*/}
        {/*          ...mentorProfile,*/}
        {/*          email: e.target.value,*/}
        {/*          emailError: "",*/}
        {/*        });*/}
        {/*      }}*/}
        {/*      value={mentorProfile.email}*/}
        {/*    />*/}
        {/*    <label className="error-message d-block">*/}
        {/*      {mentorProfile.emailError}*/}
        {/*    </label>*/}
        {/*  </Col>*/}
        {/*  <Col xs={12} md={6}>*/}
        {/*    <label style={{color: "#505050", fontWeight: "bold"}}>*/}
        {/*      Phone Number*/}
        {/*    </label>*/}
        {/*    <input*/}
        {/*      className="app-input"*/}
        {/*      onChange={(e) => {*/}
        {/*        setMentorProfile({*/}
        {/*          ...mentorProfile,*/}
        {/*          phoneNumber: e.target.value,*/}
        {/*          phoneNumberError: "",*/}
        {/*        });*/}
        {/*      }}*/}
        {/*      value={mentorProfile.phoneNumber}*/}
        {/*    />*/}
        {/*    <label className="error-message d-block">*/}
        {/*      {mentorProfile.phoneNumberError}*/}
        {/*    </label>*/}
        {/*  </Col>*/}
        {/*</Row>*/}
        <Row>
          <Col
            xs={12}
            className="text-left font-weight-bold"
            style={{ color: "rgb(7, 66, 94)", width: "100%" }}
          >
            <label style={{ color: "#505050", fontWeight: "bold" }}>Bio</label>
            <TextEditor
              initialValue={mentorProfile.bio}
              onEditorChange={onEditorUpdates}
            />
            <label className="error-message">{mentorProfile.bioError}</label>
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="d-flex">
            <div
              style={{
                width: "200px",
                flexShrink: 0,
                alignItems: "center",
              }}
            >
              <label
                className="m-0"
                style={{ color: "#505050", fontWeight: "bold" }}
              >
                Mentor Profile Picture{" "}
                <span className="ml-1" style={{ color: "red" }}>
                  *
                </span>
              </label>
              <div className="mt-3">
                <button
                  className="app-button-small"
                  onClick={() => fileInput?.click()}
                >
                  Select an image
                </button>
                <label className="error-message">
                  {mentorProfile.thumbnailError}
                </label>
                <input
                  ref={(input) => setFileInput(input)}
                  className="d-none"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    setMentorProfile({ ...mentorProfile, thumbnailError: "" });
                    const files = e.target.files;
                    if (files) {
                      let reader = new FileReader();
                      let file = files[0];
                      reader.onloadend = () => {
                        setThumbnail({
                          preview: reader.result,
                          file: file,
                        });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </div>
            </div>

            <div className=" d-flex justify-content-start">
              <img
                src={thumbnail.preview ? thumbnail.preview : ""}
                alt=""
                style={{ width: "200px" }}
              />
            </div>
          </Col>
        </Row>
        {mentorProfile.loading && (
          <div className="my-3">
            <BarLoader
              height={4}
              width={"100%"}
              color={"#006699"}
              loading={mentorProfile.loading}
            />
          </div>
        )}
        {buttonSet}
      </Container>
      {appUser.userRole.isAdmin && (
        <SelectCountry
          show={modalCountrySelectorShow}
          onHide={() => {
            setModalCountrySelectorShow(false);
          }}
        />
      )}
    </div>
  );
};

export default MentorProfileCreate;
